import { Patient } from "../models/patient.model";
import { Act } from "../models/act.model";
import { BloodGlucose } from "../models/procedure.model";
import { LabType, OldLabInfo, PROCEDURE_ROOM_LAB_TYPES } from "../models/old-labs.model";
import {
  buildLabSummaryParts,
  buildLabSummaryGrid,
  buildEgfrSummaryParts,
  buildPtSummaryParts,
  buildInrSummaryParts,
  getPregnancyTestStatusBadge,
  LabSummaryPart,
  PregnancyTestStatusBadge,
  summarizeEgfrResults,
  summarizeLabTile,
  labHasOutOfRangePanelResults,
  labHasOutOfRangePtInrResult,
} from "./lab-reference-ranges.util";

export type LabsPanelKind =
  | "full-metabolic"
  | "chem-8"
  | "pregnancy-test"
  | "egfr"
  | "pt"
  | "inr"
  | "blood-glucose"
  | "act";

export interface LabsPanelOption {
  kind: LabsPanelKind;
  label: string;
  labType?: LabType;
}

export interface LabPanelTile {
  id: string;
  kind: LabsPanelKind;
  title: string;
  summary: string;
  summaryParts?: LabSummaryPart[];
  /** Fixed 3-column grid for Chem 8 / Full Metabolic tile display. */
  summaryGrid?: (LabSummaryPart | null)[][];
  statusBadge?: PregnancyTestStatusBadge;
  recordedBy: string;
  recordedDate: string;
  labInfo?: OldLabInfo;
  bloodGlucose?: BloodGlucose;
  act?: Act;
  /** eGFR values stored on a Full Metabolic record before dedicated eGFR labs existed. */
  egfrLegacyFromFullMetabolic?: boolean;
  /** Chem 8 / Full Metabolic: any result outside panel reference range. */
  hasOutOfRangeResults?: boolean;
}

export const LABS_PANEL_OPTIONS: readonly LabsPanelOption[] = [
  { kind: "full-metabolic", label: "Full Metabolic", labType: LabType.FullMetabolic },
  { kind: "chem-8", label: "Chem 8", labType: LabType.Chem8 },
  { kind: "pregnancy-test", label: "Pregnancy Test", labType: LabType.PregnancyTest },
  { kind: "egfr", label: "eGFR", labType: LabType.EGFR },
  { kind: "pt", label: "PT", labType: LabType.PT },
  { kind: "inr", label: "INR", labType: LabType.INR },
  { kind: "blood-glucose", label: "Blood Glucose" },
  { kind: "act", label: "ACT" },
];

export function isFemalePatient(patient: Patient | null | undefined): boolean {
  return String(patient?.gender ?? "").trim().toLowerCase() === "female";
}

/** Add-menu options; Pregnancy Test is female patients only. */
export function labsPanelAddOptionsForPatient(
  patient: Patient | null | undefined
): readonly LabsPanelOption[] {
  if (isFemalePatient(patient)) {
    return LABS_PANEL_OPTIONS;
  }
  return LABS_PANEL_OPTIONS.filter((option) => option.kind !== "pregnancy-test");
}

/** Ordered add-menu options for the given panel kinds (standing diagnostic order sequence). */
export function labsPanelOptionsForKinds(
  kinds: readonly LabsPanelKind[],
  patient: Patient | null | undefined
): LabsPanelOption[] {
  const seen = new Set<LabsPanelKind>();
  const options: LabsPanelOption[] = [];
  for (const kind of kinds) {
    if (seen.has(kind)) {
      continue;
    }
    seen.add(kind);
    const option = LABS_PANEL_OPTIONS.find((o) => o.kind === kind);
    if (option) {
      options.push(option);
    }
  }
  if (isFemalePatient(patient)) {
    return options;
  }
  return options.filter((option) => option.kind !== "pregnancy-test");
}

export function labsPanelKindForLabType(type: string): LabsPanelKind | null {
  switch (String(type || "").trim()) {
    case LabType.FullMetabolic:
      return "full-metabolic";
    case LabType.Chem8:
      return "chem-8";
    case LabType.PregnancyTest:
      return "pregnancy-test";
    case LabType.EGFR:
      return "egfr";
    case LabType.PT:
      return "pt";
    case LabType.INR:
      return "inr";
    default:
      return null;
  }
}

export function labTypeForLabsPanelKind(kind: LabsPanelKind): LabType | null {
  const option = LABS_PANEL_OPTIONS.find((o) => o.kind === kind);
  return option?.labType ?? null;
}

/** Stack lab title beside tile icon: first word line 1, remainder line 2 (Chem 8 → Chem / +8). */
export function splitLabTileTitle(title: string): { line1: string; line2?: string } {
  const trimmed = String(title ?? "").trim();
  if (!trimmed) {
    return { line1: "" };
  }

  const chem8 = trimmed.match(/^chem\s+(\d+\+?)$/i);
  if (chem8) {
    return { line1: "Chem", line2: `+${chem8[1]}` };
  }

  const spaceIndex = trimmed.indexOf(" ");
  if (spaceIndex > 0) {
    return {
      line1: trimmed.slice(0, spaceIndex).trim(),
      line2: trimmed.slice(spaceIndex + 1).trim(),
    };
  }

  return { line1: trimmed };
}

export function isProcedureRoomLabRecord(lab: OldLabInfo): boolean {
  return PROCEDURE_ROOM_LAB_TYPES.includes(String(lab?.type || "").trim());
}

function hasEgfrResults(results: OldLabInfo["labResults"] | undefined): boolean {
  const r = results || {};
  return (
    (r.eGFR !== "" && r.eGFR != null) ||
    (r.maxContrast !== "" && r.maxContrast != null)
  );
}

/** True when a dedicated eGFR lab or legacy eGFR on Full Metabolic already exists. */
export function hasEgfrLabRecord(labs: OldLabInfo[]): boolean {
  const list = labs || [];
  const hasDedicated = list.some(
    (lab) => String(lab?.type || "").trim() === LabType.EGFR && !!lab?.id
  );
  if (hasDedicated) {
    return true;
  }
  const fullMetabolic = list.find(
    (lab) => String(lab?.type || "").trim() === LabType.FullMetabolic && lab?.id
  );
  return !!(fullMetabolic && hasEgfrResults(fullMetabolic.labResults));
}

function formatDisplayDateTime(raw: unknown): string {
  const value = String(raw ?? "").trim();
  if (!value) {
    return "";
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) {
    return value;
  }
  const isDateOnly = /^\d{4}-\d{2}-\d{2}$/.test(value);
  if (isDateOnly) {
    return parsed.toLocaleDateString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  }
  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function readLabStringField(
  lab: OldLabInfo | null | undefined,
  camelKey: string,
  snakeKey: string
): string {
  const raw = (lab ?? {}) as OldLabInfo & Record<string, unknown>;
  return String(raw[camelKey] ?? raw[snakeKey] ?? "").trim();
}

/** Normalizes API payloads that may use snake_case audit fields. */
export function normalizeOldLabInfo(lab: OldLabInfo): OldLabInfo {
  const raw = lab as OldLabInfo & Record<string, unknown>;
  return {
    ...lab,
    labDate: readLabStringField(lab, "labDate", "lab_date"),
    whenDate: readLabStringField(lab, "whenDate", "when_date"),
    verifiedBy: readLabStringField(lab, "verifiedBy", "verified_by"),
    verifiedAt: readLabStringField(lab, "verifiedAt", "verified_at"),
    updatedBy:
      readLabStringField(lab, "updatedBy", "updated_by") ||
      readLabStringField(lab, "createdBy", "created_by"),
    updatedAt:
      readLabStringField(lab, "updatedAt", "updated_at") ||
      readLabStringField(lab, "createdAt", "created_at"),
    clinicalOrderId:
      readLabStringField(lab, "clinicalOrderId", "clinical_order_id") ||
      undefined,
    documentId:
      readLabStringField(lab, "documentId", "document_id") || undefined,
    analyteResultFlags: normalizeAnalyteResultFlags(
      raw.analyteResultFlags ?? lab.analyteResultFlags
    ),
    labResults: raw.labResults ?? lab.labResults ?? {},
  };
}

function normalizeAnalyteResultFlags(
  value: unknown
): Record<string, string> | undefined {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    return undefined;
  }
  const out: Record<string, string> = {};
  for (const [key, raw] of Object.entries(value as Record<string, unknown>)) {
    const trimmed = String(raw ?? "").trim();
    if (trimmed) {
      out[key] = trimmed;
    }
  }
  return Object.keys(out).length > 0 ? out : undefined;
}

function getLabRecordedBy(lab: OldLabInfo): string {
  const normalized = normalizeOldLabInfo(lab);
  const by = String(
    normalized.updatedBy || normalized.verifiedBy || ""
  ).trim();
  return by || "Unknown";
}

function getLabRecordedDate(lab: OldLabInfo): string {
  const normalized = normalizeOldLabInfo(lab);
  return formatDisplayDateTime(
    normalized.updatedAt ||
      normalized.verifiedAt ||
      normalized.labDate ||
      normalized.whenDate
  );
}

function getLabRecordedTimestamp(lab: OldLabInfo): number {
  const normalized = normalizeOldLabInfo(lab);
  const raw =
    normalized.updatedAt ||
    normalized.verifiedAt ||
    normalized.labDate ||
    normalized.whenDate;
  const parsed = new Date(String(raw ?? "")).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getBloodGlucoseRecordedBy(row: BloodGlucose): string {
  const by = String(row?.updatedBy || "").trim();
  return by || "Unknown";
}

function getBloodGlucoseRecordedDate(row: BloodGlucose): string {
  const recordedAt = formatDisplayDateTime(row?.updatedAt);
  if (recordedAt) {
    return recordedAt;
  }
  return String(row?.time ?? "").trim();
}

function getBloodGlucoseRecordedTimestamp(row: BloodGlucose): number {
  const raw = row?.updatedAt || row?.time;
  const parsed = new Date(String(raw ?? "")).getTime();
  return Number.isNaN(parsed) ? 0 : parsed;
}

function getActRecordedBy(row: Act): string {
  return String(row?.createdBy ?? "").trim() || "—";
}

function getActRecordedDate(row: Act): string {
  const raw = row?.updatedAt?.trim() || row?.createdAt?.trim();
  if (!raw) {
    return "";
  }
  const d = new Date(raw);
  if (Number.isNaN(d.getTime())) {
    return raw;
  }
  return d.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

function getActRecordedTimestamp(row: Act): number {
  const raw = row?.updatedAt?.trim() || row?.createdAt?.trim();
  if (!raw) {
    return 0;
  }
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? 0 : d.getTime();
}

function buildActSummaryParts(row: Act): LabSummaryPart[] {
  const parts: LabSummaryPart[] = [];
  const value = String(row?.act ?? "").trim();
  const time = String(row?.time ?? "").trim();
  if (value) {
    parts.push({ label: "ACT:", value, outOfRange: false });
  }
  if (time) {
    const timeLabel = /^\d{1,2}:\d{2}/.test(time) ? "Time:" : "Result:";
    parts.push({ label: timeLabel, value: time, outOfRange: false });
  }
  return parts;
}

function bloodGlucoseSecondaryLabel(time: string): string {
  if (/^\d{1,2}:\d{2}/.test(time)) {
    return "Time:";
  }
  return "Result:";
}

function buildBloodGlucoseSummaryParts(row: BloodGlucose): LabSummaryPart[] {
  const parts: LabSummaryPart[] = [];
  const value = String(row?.name ?? "").trim();
  const time = String(row?.time ?? "").trim();
  if (value) {
    parts.push({
      label: "Glucose:",
      value,
      outOfRange: false,
    });
  }
  if (time) {
    parts.push({
      label: bloodGlucoseSecondaryLabel(time),
      value: time,
      outOfRange: false,
    });
  }
  return parts;
}

function getTileSortTimestamp(tile: LabPanelTile): number {
  if (tile.act) {
    return getActRecordedTimestamp(tile.act);
  }
  if (tile.bloodGlucose) {
    return getBloodGlucoseRecordedTimestamp(tile.bloodGlucose);
  }
  if (tile.labInfo) {
    return getLabRecordedTimestamp(tile.labInfo);
  }
  return 0;
}

export function buildLabPanelTiles(
  labs: OldLabInfo[],
  bloodGlucoseRows: BloodGlucose[],
  actRows: Act[] = []
): LabPanelTile[] {
  const tiles: LabPanelTile[] = [];

  for (const lab of labs || []) {
    if (!lab?.id || !isProcedureRoomLabRecord(lab)) {
      continue;
    }
    const kind = labsPanelKindForLabType(lab.type);
    if (!kind) {
      continue;
    }
    const option = LABS_PANEL_OPTIONS.find((o) => o.kind === kind);
    const summaryParts =
      kind === "egfr"
        ? buildEgfrSummaryParts(lab.labResults)
        : kind === "pt"
          ? buildPtSummaryParts(lab.labResults)
          : kind === "inr"
            ? buildInrSummaryParts(lab.labResults)
            : buildLabSummaryParts(lab);
    const summaryGrid = buildLabSummaryGrid(lab);
    const statusBadge =
      kind === "pregnancy-test" ? getPregnancyTestStatusBadge(lab) ?? undefined : undefined;
    tiles.push({
      id: lab.id,
      kind,
      title: option?.label || lab.type,
      summary: summarizeLabTile(lab),
      summaryParts: summaryParts.length ? summaryParts : undefined,
      summaryGrid: summaryGrid ?? undefined,
      statusBadge,
      hasOutOfRangeResults:
        kind === "chem-8" || kind === "full-metabolic"
          ? labHasOutOfRangePanelResults(lab)
          : kind === "pt" || kind === "inr"
            ? labHasOutOfRangePtInrResult(lab)
            : undefined,
      recordedBy: getLabRecordedBy(lab),
      recordedDate: getLabRecordedDate(lab),
      labInfo: lab,
    });
  }

  const hasDedicatedEgfrLab = (labs || []).some(
    (lab) => String(lab?.type || "").trim() === LabType.EGFR && !!lab?.id
  );
  if (!hasDedicatedEgfrLab) {
    const fullMetabolic = (labs || []).find(
      (lab) => String(lab?.type || "").trim() === LabType.FullMetabolic && lab?.id
    );
    if (fullMetabolic && hasEgfrResults(fullMetabolic.labResults)) {
      tiles.push({
        id: `egfr-legacy-${fullMetabolic.id}`,
        kind: "egfr",
        title: "eGFR",
        summary: summarizeEgfrResults(fullMetabolic.labResults),
        summaryParts: buildEgfrSummaryParts(fullMetabolic.labResults),
        recordedBy: getLabRecordedBy(fullMetabolic),
        recordedDate: getLabRecordedDate(fullMetabolic),
        labInfo: fullMetabolic,
        egfrLegacyFromFullMetabolic: true,
      });
    }
  }

  for (const row of bloodGlucoseRows || []) {
    if (!row?.id) {
      continue;
    }
    const value = String(row.name ?? "").trim();
    const time = String(row.time ?? "").trim();
    const summaryParts = buildBloodGlucoseSummaryParts(row);
    let summary = value || "Recorded";
    if (time) {
      summary =
        /^\d{1,2}:\d{2}/.test(time) && value
          ? `${value} at ${time}`
          : [value, time].filter(Boolean).join(" · ") || summary;
    }
    tiles.push({
      id: row.id,
      kind: "blood-glucose",
      title: "Blood Glucose",
      summary,
      summaryParts: summaryParts.length ? summaryParts : undefined,
      recordedBy: getBloodGlucoseRecordedBy(row),
      recordedDate: getBloodGlucoseRecordedDate(row),
      bloodGlucose: row,
    });
  }

  for (const row of actRows || []) {
    if (!row?.id) {
      continue;
    }
    const value = String(row.act ?? "").trim();
    const time = String(row.time ?? "").trim();
    const summaryParts = buildActSummaryParts(row);
    tiles.push({
      id: row.id,
      kind: "act",
      title: "ACT",
      summary: [value, time].filter(Boolean).join(" at ") || "Recorded",
      summaryParts: summaryParts.length ? summaryParts : undefined,
      recordedBy: getActRecordedBy(row),
      recordedDate: getActRecordedDate(row),
      act: row,
    });
  }

  return tiles.sort(
    (a, b) => getTileSortTimestamp(b) - getTileSortTimestamp(a)
  );
}
